import { PAGE_SIZE_OPTIONS, type PageSize } from '../hooks/usePagination'

interface Props {
  page: number
  setPage: (p: number) => void
  pageSize: PageSize
  setPageSize: (s: PageSize) => void
  total: number | null
}

// Z56: shared "Showing X-Y of Z" + page-size selector + prev/next for any list page using
// usePagination. `total` is null while the count query is still loading.
export default function PaginationControls({ page, setPage, pageSize, setPageSize, total }: Props) {
  const effectiveTotal = total ?? 0
  const from = effectiveTotal === 0 ? 0 : (pageSize === 'all' ? 1 : page * pageSize + 1)
  const to = pageSize === 'all' ? effectiveTotal : Math.min(effectiveTotal, page * pageSize + pageSize)
  const pageCount = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(effectiveTotal / pageSize))

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-600 dark:text-gray-400">
      <div>
        {total === null ? 'Loading…' : `Showing ${from}-${to} of ${effectiveTotal}`}
      </div>
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-1.5">
          <span>Per page</span>
          <select
            value={String(pageSize)}
            onChange={e => {
              const next = e.target.value === 'all' ? 'all' : (parseInt(e.target.value, 10) as PageSize)
              // Z35.1: "All" on a large list fetches every row in one request -- confirm first
              // rather than silently blocking the tab on an unbounded query.
              if (next === 'all' && total !== null && total > 500) {
                const ok = window.confirm(`Loading all ${total} rows may be slow — proceed?`)
                if (!ok) return
              }
              setPageSize(next)
            }}
            className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            {PAGE_SIZE_OPTIONS.map(n => (
              <option key={n} value={n}>{n}</option>
            ))}
            <option value="all">All</option>
          </select>
        </label>
        {pageSize !== 'all' && (
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="px-2.5 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Prev
            </button>
            <span>Page {page + 1} of {pageCount}</span>
            <button
              onClick={() => setPage(Math.min(pageCount - 1, page + 1))}
              disabled={page >= pageCount - 1}
              className="px-2.5 py-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
